import { ButtonType, DialogFooterButtonsWidget, DialogPositioningBehavior, DialogType, DialogWidget, RadioButtonWithLabelGroupWidget, RadioButtonWithLabelOption } from '@tableau/widgets';
import * as React from 'react';
import { Formatters, Messages } from '../../codegen/Localize';
import { LockUnlockPermissionsState } from '../../ts/helpers/LockStatusHelper';
import { L10n } from '../../ts/L10n/L10n';

const DIALOG_WIDTH = 446; // makes the dialog content 400px given the padding of DialogWidget
export enum PERMISSIONS_LOCK_RADIO_INDICIES {
  Mixed = -1, // negative because in the mixed state no radio button will be selected
  Locked = 0,
  Unlocked = 1,
}

const RadioIndexToLockState: {[key: number]: LockUnlockPermissionsState} = Object.freeze({
  [PERMISSIONS_LOCK_RADIO_INDICIES.Mixed]: LockUnlockPermissionsState.Mixed,
  [PERMISSIONS_LOCK_RADIO_INDICIES.Locked]: LockUnlockPermissionsState.Locked,
  [PERMISSIONS_LOCK_RADIO_INDICIES.Unlocked]: LockUnlockPermissionsState.Unlocked,
});

const LockStateToRadioIndex: {[key: number]: PERMISSIONS_LOCK_RADIO_INDICIES} = Object.freeze({
  [LockUnlockPermissionsState.Locked]: PERMISSIONS_LOCK_RADIO_INDICIES.Locked,
  [LockUnlockPermissionsState.Unlocked]: PERMISSIONS_LOCK_RADIO_INDICIES.Unlocked,
  [LockUnlockPermissionsState.Mixed]: PERMISSIONS_LOCK_RADIO_INDICIES.Mixed,
});


export type PermissionsLockDialogProps = {
  /** The number of projects in this dialog. */
  projectsNumber: number;

  /** Name of the (first) project in this dialog. */
  projectName: string;

  /** Callback invoked when the dialog's cancel button is clicked. */
  onCancel: () => void;

  /** Callback invoked when the dialog's save button is clicked.
   * @param lockState the chosen lock state that will be saved
   */
  onSave: (lockState: LockUnlockPermissionsState) => void;

  /** The original lock state of the project(s) when the dialog is first opened */
  currentLockStatus: LockUnlockPermissionsState;
};

export type PermissionsLockDialogState = {
  checkedIndex: number;
};

export class PermissionsLockDialog extends React.Component<PermissionsLockDialogProps, PermissionsLockDialogState> {
  public state: PermissionsLockDialogState = {
      checkedIndex: LockStateToRadioIndex[this.props.currentLockStatus]
  };

  private getCurrentStatusAsRadioValue = (): PERMISSIONS_LOCK_RADIO_INDICIES => {
    return LockStateToRadioIndex[this.props.currentLockStatus];
  }

  private handleChangeLockOption = (checkedValue: LockUnlockPermissionsState, checkedIndex: number): void => {
    this.setState({ checkedIndex });
  }

  private willLockProjects = (): boolean => {
    return (this.props.currentLockStatus !== LockUnlockPermissionsState.Locked)
      && (this.state.checkedIndex === PERMISSIONS_LOCK_RADIO_INDICIES.Locked);
  }

  private isSaveDisabled = (): boolean => {
    // disabled if the current selection wouldn't result in a change to the lock status
    return this.state.checkedIndex === PERMISSIONS_LOCK_RADIO_INDICIES.Mixed || this.state.checkedIndex === this.getCurrentStatusAsRadioValue();
  }

  private handleSave = (): void => {
    this.props.onSave(RadioIndexToLockState[this.state.checkedIndex]);
  }

  private renderWarningMessage = (): JSX.Element => {
    return (
      <div>
        <p className="tb-permissions-warning-message" data-tb-test-id="permissions-lock-dialog-warning-msg">
          { Messages.lockProjectPermissionsWarningMessage({
              PROJECT_COUNT: this.props.projectsNumber,
              PROJECT_COUNT_LOC: Formatters.formatCount(this.props.projectsNumber)
          })}
        </p>
      </div>
    );
  }

  private renderFooter = (): JSX.Element => {
    return (
      <DialogFooterButtonsWidget
        buttons={[
          {
            handleClick: this.props.onCancel,
            buttonType: ButtonType.Grey,
            children: Messages.cancel(),
            testId: 'permissions-lock-dialog-cancel'
          }, {
            handleClick: this.handleSave,
            buttonType: ButtonType.Go,
            children: Messages.save(),
            testId: 'permissions-lock-dialog-save',
            disabled: this.isSaveDisabled()
          }
        ]}
        testId="permissions-lock-dialog"
      />
    );
  }

  private renderLockedRadioButtonLabel = (): JSX.Element => {
    return (
      <span>
        <span>
          <span className="tb-gray-icon-permissions-locked tb-lock-icon"/>
          <span
            className="tb-permission-option tb-radio-button-label-react"
          >
            { Messages.lockProjectPermissions_locked() }
          </span>
        </span>
        <div>
          <p className="tb-dialog-lock-description">{ Messages.lockProjectPermissions_lockedDescription() }</p>
        </div>
        { this.willLockProjects() && this.renderWarningMessage() }
      </span>
    );
  }

  private renderUnlockedRadioButtonLabel = (): JSX.Element => {
    return (
      <span>
        <span>
          <span className="tb-gray-icon-permissions-unlocked tb-lock-icon"/>
          <span
            className="tb-permission-option tb-radio-button-label-react"
          >
            { Messages.lockProjectPermissions_unlocked() }
          </span>
        </span>
        <div>
          <p className="tb-dialog-lock-description">{ Messages.lockProjectPermissions_unlockedDescription() }</p>
        </div>
      </span>
    );
  }

  public render (): JSX.Element {
    // label any type assertion is an approved workaround by the Widgets team because this older design
    // calls for a more complex label than just a string (what all newer designs should have)
    const radioButtons: RadioButtonWithLabelOption<LockUnlockPermissionsState>[] = [
      {
        label: this.renderLockedRadioButtonLabel() as any,
        testId: 'locked-to-the-project',
        value: LockUnlockPermissionsState.Locked
      }, {
        label: this.renderUnlockedRadioButtonLabel() as any,
        testId: 'managed-by-the-owner',
        value: LockUnlockPermissionsState.Unlocked
      }
    ];
    return (
      <DialogWidget
        dialogWidth={ DIALOG_WIDTH }
        isModal={ true }
        onCloseRequest={ this.props.onCancel }
        positioningBehavior={ DialogPositioningBehavior.Centered }
        showCloseButton={ false }
        showFooter={ true }
        footerContent={ this.renderFooter() }
        handleGlassClick={ this.props.onCancel }
        testId="permissions-lock"
        title={ Messages.lockProjectPermissions_dialogTitle() }
        type={ DialogType.LightweightTitleBar }
      >
        <p data-tb-test-id="permissions-lock-dialog-msg" className="tb-dialog-body-text">
          { Messages.lockProjectPermissions_dialogMessage({
              PROJECT_COUNT: this.props.projectsNumber,
              PROJECT_NAME: L10n.elideIfLong(this.props.projectName),
              PROJECT_COUNT_LOC: L10n.localizedCount(this.props.projectsNumber)
          }) }
        </p>
        <div className="tb-lock-unlock-permissions-dialog tb-dialog-body-text">
          <RadioButtonWithLabelGroupWidget
            buttonList={ radioButtons }
            checkedIndex={ this.state.checkedIndex }
            handleChange={ this.handleChangeLockOption }
            testId="permissions-lock-dialog-radio-btns"
          />
        </div>
      </DialogWidget>
    );
  }
}
